import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { useFilters } from '@/lib/filters';
import { formatIsoDate } from '@/lib/format';
import { useViewMessages } from '@/lib/i18n';
import { osloDay } from '@/lib/reporting-days';
import { cn } from '@/lib/utils';

const MESSAGES = {
  nb: {
    today: 'I går',
    last7: 'Siste 7 dager',
    last14: 'Siste 14 dager',
    last30: 'Siste 30 dager',
    last90: 'Siste 90 dager',
    thisMonth: 'Denne måneden',
    lastMonth: 'Forrige måned',
    apply: 'Bruk',
    pickEnd: 'velg sluttdato',
    cancel: 'Avbryt',
    weekdays: ['ma', 'ti', 'on', 'to', 'fr', 'lø', 'sø'],
    months: [
      'Januar',
      'Februar',
      'Mars',
      'April',
      'Mai',
      'Juni',
      'Juli',
      'August',
      'September',
      'Oktober',
      'November',
      'Desember',
    ],
  },
  en: {
    today: 'Yesterday',
    last7: 'Last 7 days',
    last14: 'Last 14 days',
    last30: 'Last 30 days',
    last90: 'Last 90 days',
    thisMonth: 'This month',
    lastMonth: 'Last month',
    apply: 'Apply',
    pickEnd: 'pick end date',
    cancel: 'Cancel',
    weekdays: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
    months: [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ],
  },
};

function iso(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}
function parseIso(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(Date.UTC(y ?? 2000, (m ?? 1) - 1, d ?? 1, 12));
}
function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setUTCDate(x.getUTCDate() + n);
  return x;
}
function startOfMonth(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1, 12));
}
function addMonths(d: Date, n: number): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n, 1, 12));
}

/** Monday-first 6×7 grid of dates for the month containing `viewMonth`. */
function monthGrid(viewMonth: Date): Date[] {
  const first = startOfMonth(viewMonth);
  // JS getDay: 0=Sun … 6=Sat. Convert to Monday-first offset.
  const offset = (first.getUTCDay() + 6) % 7;
  const start = addDays(first, -offset);
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}

export function DateRangePicker() {
  const m = useViewMessages(MESSAGES);
  const { from, to, setRange } = useFilters();
  // Taket i velgeren er I DAG. «I dag»-knappen ga i går, fordi hele kalenderen
  // var klampet til siste hele døgn. Bare vekstregnskapet trenger hele dager, og
  // det klamper seg selv på serveren (resolveCompleteReportingRange) samtidig som
  // scorecardet skriver ut «Valgt periode … Effektiv periode …».
  const completeThrough = osloDay();
  const completeDate = parseIso(completeThrough);
  const reportingTodayDate = completeDate;
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  // The popover is portaled to <body> (escapes the sticky FilterBar's stacking
  // context, which otherwise let the table paint over it). Position it under the
  // trigger with fixed coords from the button's rect.
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  // draft selection (only committed on Apply / preset)
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState<string | null>(to);
  const [view, setView] = useState(() => startOfMonth(parseIso(to)));
  // while picking the end date, preview the range under the cursor
  const [hover, setHover] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setDraftFrom(from);
    setDraftTo(to);
    setView(startOfMonth(parseIso(to)));
    // Clamp the panel into the viewport so it never renders off-screen (esp. on
    // mobile, where it's a near-full-width sheet). Recompute on resize/scroll.
    const place = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const panelW = window.innerWidth < 640 ? window.innerWidth - 16 : 416;
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - panelW - 8));
      setPos({ top: rect.bottom + 8, left });
    };
    place();
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || popRef.current?.contains(t)) return;
      setOpen(false);
    };
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    document.addEventListener('mousedown', onDoc);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      document.removeEventListener('mousedown', onDoc);
    };
  }, [open, from, to]);

  const grid = useMemo(() => monthGrid(view), [view]);

  function applyPreset(days: number) {
    setRange(iso(addDays(completeDate, -(days - 1))), completeThrough);
    setOpen(false);
  }
  function applyMonth(offset: number) {
    const first = addMonths(reportingTodayDate, offset);
    const next = addMonths(reportingTodayDate, offset + 1);
    const last = addDays(next, -1);
    setRange(
      iso(first) > completeThrough ? completeThrough : iso(first),
      iso(last) > completeThrough ? completeThrough : iso(last),
    );
    setOpen(false);
  }
  function pickDay(d: Date) {
    const s = iso(d);
    if (s > completeThrough) return;
    if (draftTo === null) {
      // second click → choose the end (swap if before start)
      if (s >= draftFrom) setDraftTo(s);
      else {
        setDraftTo(draftFrom);
        setDraftFrom(s);
      }
    } else {
      // a range is already drafted → start a new one
      setDraftFrom(s);
      setDraftTo(null);
    }
  }
  function applyDraft() {
    if (draftTo) {
      setRange(draftFrom, draftTo);
      setOpen(false);
    }
  }

  // Each preset knows its own range so the active one can be marked — without
  // that, the panel never tells you WHICH period you are looking at.
  function daysRange(days: number): [string, string] {
    return [iso(addDays(completeDate, -(days - 1))), completeThrough];
  }
  function monthRange(offset: number): [string, string] {
    const first = addMonths(reportingTodayDate, offset);
    const last = addDays(addMonths(reportingTodayDate, offset + 1), -1);
    return [
      iso(first) > completeThrough ? completeThrough : iso(first),
      iso(last) > completeThrough ? completeThrough : iso(last),
    ];
  }
  const presets: { label: string; range: [string, string]; fn: () => void }[] = [
    { label: m.today, range: daysRange(1), fn: () => applyPreset(1) },
    { label: m.last7, range: daysRange(7), fn: () => applyPreset(7) },
    { label: m.last14, range: daysRange(14), fn: () => applyPreset(14) },
    { label: m.last30, range: daysRange(30), fn: () => applyPreset(30) },
    { label: m.last90, range: daysRange(90), fn: () => applyPreset(90) },
    { label: m.thisMonth, range: monthRange(0), fn: () => applyMonth(0) },
    { label: m.lastMonth, range: monthRange(-1), fn: () => applyMonth(-1) },
  ];
  const activePreset = presets.find((p) => p.range[0] === from && p.range[1] === to)?.label ?? null;

  const fromLabel = formatIsoDate(from).date;
  const toLabel = formatIsoDate(to).date;
  const viewMonthLabel = `${m.months[view.getUTCMonth()]} ${view.getUTCFullYear()}`;
  const canGoNext = iso(addMonths(view, 1)) <= completeThrough;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex h-9 items-center gap-2 rounded-md border border-border bg-surface px-3 text-label text-fg-muted transition-fast hover:bg-surface-2 sm:h-8"
      >
        <CalendarDays className="size-3.5 text-fg-subtle" aria-hidden />
        <span className="tabular text-fg">
          {fromLabel} – {toLabel}
        </span>
      </button>

      {open
        ? createPortal(
            <div
              ref={popRef}
              style={{ position: 'fixed', top: pos.top, left: pos.left }}
              className="z-[70] flex max-h-[calc(100vh-5rem)] w-[calc(100vw-1rem)] max-w-[26rem] flex-col overflow-y-auto rounded-lg border border-border bg-surface shadow-pop sm:max-h-none sm:w-auto sm:max-w-none sm:flex-row sm:overflow-visible"
            >
              {/* presets — a wrapping chip row on mobile, a column on sm+ */}
              <div className="flex w-full flex-wrap gap-1 border-b border-border p-2 sm:w-40 sm:flex-col sm:flex-nowrap sm:gap-0.5 sm:border-r sm:border-b-0">
                {presets.map((p) => {
                  const isActive = p.label === activePreset;
                  return (
                    <button
                      key={p.label}
                      type="button"
                      onClick={p.fn}
                      aria-current={isActive ? 'true' : undefined}
                      className={cn(
                        'rounded-md px-2.5 py-1.5 text-left text-label transition-fast',
                        isActive
                          ? 'bg-brand-tint font-medium text-brand'
                          : 'text-fg-muted hover:bg-surface-2 hover:text-fg',
                      )}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </div>

              {/* calendar */}
              <div className="w-full p-3 sm:w-64">
                {/* what you are about to apply — Shopify's Starting/Ending line */}
                <div className="mb-2 flex items-center gap-1.5 rounded-md border border-border bg-surface-2 px-2 py-1.5 text-micro tabular">
                  <span className={cn(draftTo === null && 'font-semibold text-brand')}>
                    {formatIsoDate(draftFrom).date}
                  </span>
                  <span className="text-fg-subtle">–</span>
                  <span className={cn(draftTo === null ? 'text-fg-subtle' : 'text-fg')}>
                    {draftTo ? formatIsoDate(draftTo).date : m.pickEnd}
                  </span>
                </div>
                <div className="mb-2 flex items-center justify-between">
                  <button
                    type="button"
                    aria-label="forrige"
                    onClick={() => setView(addMonths(view, -1))}
                    className="rounded p-1 text-fg-subtle transition-fast hover:bg-surface-2 hover:text-fg"
                  >
                    <ChevronLeft className="size-4" />
                  </button>
                  <span className="text-label font-medium text-fg">{viewMonthLabel}</span>
                  <button
                    type="button"
                    aria-label="neste"
                    onClick={() => setView(addMonths(view, 1))}
                    disabled={!canGoNext}
                    className="rounded p-1 text-fg-subtle transition-fast hover:bg-surface-2 hover:text-fg disabled:cursor-not-allowed disabled:opacity-30"
                  >
                    <ChevronRight className="size-4" />
                  </button>
                </div>

                <div className="grid grid-cols-7 gap-0.5 text-center text-micro text-fg-subtle">
                  {m.weekdays.map((w) => (
                    <div key={w} className="py-1">
                      {w}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-0.5">
                  {grid.map((d) => {
                    const s = iso(d);
                    const inMonth = d.getUTCMonth() === view.getUTCMonth();
                    const future = s > completeThrough;
                    const isStart = s === draftFrom;
                    const isEnd = s === draftTo;
                    const previewEnd =
                      draftTo === null && hover && hover > draftFrom ? hover : null;
                    const inRange =
                      (draftTo !== null && s > draftFrom && s < draftTo) ||
                      (previewEnd !== null && s > draftFrom && s <= previewEnd);
                    return (
                      <button
                        key={s}
                        type="button"
                        disabled={future}
                        onClick={() => pickDay(d)}
                        onMouseEnter={() => setHover(s)}
                        onMouseLeave={() => setHover(null)}
                        className={cn(
                          'flex h-9 items-center justify-center rounded text-label tabular transition-fast sm:h-8',
                          !inMonth && 'text-fg-subtle/40',
                          future && 'cursor-not-allowed text-fg-subtle/25',
                          inMonth &&
                            !future &&
                            !isStart &&
                            !isEnd &&
                            !inRange &&
                            'text-fg hover:bg-surface-2',
                          inRange && 'bg-brand-tint text-brand',
                          (isStart || isEnd) && 'bg-brand font-medium text-brand-fg',
                        )}
                      >
                        {d.getUTCDate()}
                      </button>
                    );
                  })}
                </div>

                <div className="mt-2 flex items-center justify-end gap-2 border-t border-border pt-2">
                  <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
                    {m.cancel}
                  </Button>
                  <Button variant="primary" size="sm" onClick={applyDraft} disabled={!draftTo}>
                    {m.apply}
                  </Button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
